import { useCookieOptions } from '~/composables/useCookieOptions';
import { type AuthErrorCode, getAuthErrorCodes } from '~/constants/errorCodes';
import { STORAGE_KEYS } from '~/constants/storageKeys';
import { useOrganizationStore } from '~/stores/organization';
import { useLocalePath } from '#i18n';

// 定義支援的語系
const SUPPORTED_LOCALES = ['en', 'zh-TW'] as const;
const DEFAULT_LOCALE = 'en';

// 定義 API 錯誤類型
interface ApiError {
  status?: number;
  statusCode?: number;
  code?: number | string;
  message?: string;
}

// 定義公開路徑（不需要登入的路徑）
const PUBLIC_PATHS = ['login', 'signup', 'forgot-password', 'auth/callback'] as const;
// 定義需要重定向到 dashboard 的路徑
const REDIRECT_TO_DASHBOARD_PATHS = ['login', 'signup', 'forgot-password'] as const;

// 輔助函數：解析路徑中的語系
const parseLocaleFromPath = (path: string) => {
  // 移除開頭的斜線
  const pathWithoutLeadingSlash = path.replace(/^\/+/, '');

  // 匹配語系部分（確保語系後面有斜線或結尾）
  const localeMatch = pathWithoutLeadingSlash.match(/^([a-z]{2}(-[a-z]{2})?)(\/|$)/i);
  if (!localeMatch) {
    return {
      locale: DEFAULT_LOCALE,
      pathWithoutLocale: path,
    };
  }

  const matchedLocale = localeMatch[1];
  if (!SUPPORTED_LOCALES.includes(matchedLocale as (typeof SUPPORTED_LOCALES)[number])) {
    return {
      locale: DEFAULT_LOCALE,
      pathWithoutLocale: path,
    };
  }

  // 移除語系部分，保留剩餘路徑
  const pathWithoutLocale = pathWithoutLeadingSlash.replace(/^[a-z]{2}(-[a-z]{2})?(\/|$)/i, '');
  return {
    locale: matchedLocale,
    pathWithoutLocale: pathWithoutLocale ? `/${pathWithoutLocale}` : '/',
  };
};

// 輔助函數：將路徑轉換為小寫並移除開頭的斜線
const normalizePath = (path: string) => path.toLowerCase().replace(/^\/+/, '');

// 輔助函數：安全地取得錯誤狀態碼
const getErrorStatus = (error: unknown): number | undefined => {
  if (error && typeof error === 'object') {
    const apiError = error as ApiError;
    return apiError.status ?? apiError.statusCode;
  }
  return undefined;
};

// 輔助函數：安全地取得錯誤代碼
const getErrorCode = (error: unknown): number | string | undefined => {
  if (error && typeof error === 'object') {
    const apiError = error as ApiError;
    return apiError.code;
  }
  return undefined;
};

// 輔助函數：安全地取得錯誤訊息
const getErrorMessage = (error: unknown): string | undefined => {
  if (error instanceof Error) {
    return error.message;
  }
  if (error && typeof error === 'object') {
    const apiError = error as ApiError;
    return apiError.message;
  }
  return undefined;
};

export default defineNuxtRouteMiddleware(async (to) => {
  const authStore = useAuthStore();
  const organizationStore = useOrganizationStore();
  const localePath = useLocalePath();
  const auth = useAuth();

  let from_url = '';
  if (import.meta.client) {
    from_url = localStorage.getItem('from_url') || '';
    localStorage.removeItem('from_url');
  }

  // 如果是 Chrome DevTools 的請求，直接放行
  if (to.path === '/.well-known/appspecific/com.chrome.devtools.json') {
    return true;
  }

  // 解析路徑中的語系
  const { pathWithoutLocale } = parseLocaleFromPath(to.path);

  // 標準化路徑以便比較
  const normalizedPath = normalizePath(pathWithoutLocale);

  // 輔助函數：從 URL 中手動解析 query 參數（處理異常格式，如多個 ?）
  const parseQueryParams = (): Record<string, string> => {
    const params: Record<string, string> = {};

    if (import.meta.client && typeof window !== 'undefined') {
      // 在客戶端，從 window.location.search 解析
      const search = window.location.search;
      if (search) {
        // 處理多個 ? 的情況：window.location.search 以 ? 開頭，所以將第二個及之後的 ? 替換為 &
        // 例如：?invite_code=123?guest_user_uuid=xxx -> ?invite_code=123&guest_user_uuid=xxx
        const normalizedSearch = search.replace(/\?(?=.*\?)/g, '&');
        try {
          const urlParams = new URLSearchParams(normalizedSearch);
          for (const [key, value] of urlParams.entries()) {
            params[key] = value;
          }
        } catch (error) {
          console.warn('Failed to parse query params:', error);
        }
      }
    }

    // 合併 to.query 中的參數（Nuxt 解析的參數，作為備用）
    for (const [key, value] of Object.entries(to.query)) {
      if (value !== null && value !== undefined) {
        // 處理陣列情況
        const stringValue = Array.isArray(value) ? value[0] : String(value);
        if (stringValue && !params[key]) {
          // 只有在手動解析沒有該參數時才使用 to.query 的值
          params[key] = stringValue;
        }
      }
    }

    return params;
  };

  // 解析 query 參數（處理異常格式）
  const queryParams = parseQueryParams();

  // 檢查是否為 Google OAuth 2FA 回調
  const is2FACallback = queryParams.is_2fa_enabled === 'true' && queryParams.provisional_2fa_token;
  // 檢查是否為 Google OAuth guest user 回調
  const isGuestUserCallback = !!queryParams.guest_user_uuid;

  if (authStore.isLoggedIn()) {
    // 如果是 2FA 回調且目標是 login 頁面，允許通過
    if (is2FACallback && normalizedPath === 'login') {
      // console.log('✅ Allowing 2FA callback to login page');
      return true;
    }
    // 如果是 guest_user_uuid 回調且目標是 login 頁面，允許通過
    if (isGuestUserCallback && normalizedPath === 'login') {
      return true;
    }

    // 如果用戶已登入，檢查組織權限
    try {
      const currentOrg = await organizationStore.getCurrentOrganization();

      // 如果 currentOrg 為 null，表示用戶沒有組織，導向到 organization/setup
      if (!currentOrg) {
        if (normalizedPath === 'organization/setup') {
          return true;
        }
        return navigateTo({
          path: localePath('/organization/setup'),
          query: to.query,
        });
      }

      if (currentOrg.is_current) {
        if (import.meta.client) {
          // 使用統一的 cookie 選項，確保 domain 設定正確
          const orgUuidCookie = useCookie(STORAGE_KEYS.ORGUUID, useCookieOptions());
          orgUuidCookie.value = currentOrg.organizations_uuid;
        }

        // 如果是 2FA 回調，將參數傳遞到 login 頁面
        if (is2FACallback) {
          // console.log('🔄 Redirecting 2FA callback to login page');
          return navigateTo({
            path: localePath('/login'),
            query: {
              is_2fa_enabled: to.query.is_2fa_enabled,
              provisional_2fa_token: to.query.provisional_2fa_token,
            },
          });
        }

        if (
          REDIRECT_TO_DASHBOARD_PATHS.includes(
            normalizedPath as (typeof REDIRECT_TO_DASHBOARD_PATHS)[number]
          )
        ) {
          const redirectDataObj = auth.getRedirectUrl();

          if (redirectDataObj?.path) {
            const { path, query } = redirectDataObj;
            // 使用統一的 cookie 選項，確保 domain 設定正確
            useCookie(STORAGE_KEYS.REDIRECT_AFTER_LOGIN, useCookieOptions<string>()).value = '';
            localStorage.removeItem(STORAGE_KEYS.REDIRECT_AFTER_LOGIN);
            return navigateTo({ path, query });
          }
          //TODO for develop
          // return true
          return navigateTo(localePath('/dashboard'));
        }
        // 正常訪問
        return true;
      }
      if (normalizedPath === 'organization/setup') {
        return true;
      }
      return navigateTo({
        path: localePath('/organization/setup'),
        query: to.query,
      });
    } catch (error) {
      // 處理認證錯誤
      if (error instanceof Error && error.message.includes('Not authenticated')) {
        await auth.refreshTokenOnUnauthorized();
        return true;
      }

      // 檢查是否為「目前不在任何組織中」的錯誤
      // API 錯誤格式: {"code":400,"message":"Currently not in any organization","error":"bad_request"}
      // 從 useApiFetch 拋出的錯誤是 Error 物件，會保留 error.code 和 error.message 屬性
      const errorCode = getErrorCode(error);
      const errorMessage = getErrorMessage(error);

      // 檢查是否為「沒有組織」的錯誤
      // 支援多種錯誤格式：
      // 1. error.code === 400 && message matches
      // 2. error.status === 400 && message matches
      // 3. error.statusCode === 400 && message matches
      const errorStatus = (error as ApiError)?.status || (error as ApiError)?.statusCode;
      const is400Error =
        errorCode === 400 ||
        errorCode === '400' ||
        Number(errorCode) === 400 ||
        errorStatus === 400;
      const isNoOrganizationMessage = errorMessage === 'Currently not in any organization';
      const isNoOrganizationError = is400Error && isNoOrganizationMessage;

      if (isNoOrganizationError) {
        // 只有當錯誤是「目前不在任何組織中」時，才導向到 organization/setup
        if (normalizedPath !== 'organization/setup') {
          return navigateTo(
            {
              path: localePath('/organization/setup'),
              query: to.query,
            },
            { replace: true }
          );
        }
        return true;
      }

      // 如果已經在 organization/setup 頁面，允許訪問（避免循環重定向）
      // 因為這個頁面本身就是用來處理「沒有組織」的情況
      if (normalizedPath === 'organization/setup') {
        return true;
      }

      // 其他錯誤導向到登入頁面（避免在公開頁面造成循環重定向）
      if (
        normalizedPath !== 'login' &&
        normalizedPath !== 'signup' &&
        normalizedPath !== 'forgot-password' &&
        normalizedPath !== 'auth/callback'
      ) {
        // 根據錯誤狀態碼決定錯誤類型，透過 query 參數傳遞給登入頁
        const errorStatus = getErrorStatus(error);
        const errorType = errorStatus === 401 ? 'loginExpired' : 'unexpectedError';

        return navigateTo({
          path: localePath('/login'),
          query: {
            ...to.query,
            authError: errorType,
          },
        });
      }
      return true;
    }
  } else {
    // 如果用戶未登入且不在公開頁面（login/signup/forgot-password/auth/callback），重定向到登入頁面
    if (!PUBLIC_PATHS.includes(normalizedPath as (typeof PUBLIC_PATHS)[number])) {
      //TODO for develop
      // return true
      // 保存原始 URL 和 query 參數，以便登入後重定向
      const redirectData = {
        path: to.path,
        query: to.query,
      };
      const is_register = to.query.is_register as string;
      if (is_register === 'false') {
        redirectData.path = localePath('/organization/setup');
      }
      try {
        const json = JSON.stringify(redirectData);
        // 以 cookie 保存，與 localStorage 並存（供回退）
        // 使用統一的 cookie 選項，確保 domain 設定正確
        const redirectCookie = useCookie<string>(
          STORAGE_KEYS.REDIRECT_AFTER_LOGIN,
          useCookieOptions({
            sameSite: 'lax',
            maxAge: 60 * 10, // 10 分鐘
          })
        );
        redirectCookie.value = json;
        if (import.meta.client) {
          localStorage.setItem(STORAGE_KEYS.REDIRECT_AFTER_LOGIN, json);
        }
      } catch (_) {
        if (import.meta.client) {
          localStorage.setItem(STORAGE_KEYS.REDIRECT_AFTER_LOGIN, JSON.stringify(redirectData));
        }
      }

      // 如果 is_register === 'false'，沿用原本邏輯導向註冊頁（與 guest_user 流程無關）
      if (is_register === 'false') {
        return navigateTo({
          path: localePath('/signup'),
          query: to.query,
        });
      }

      const err_code = to.query.err_code as unknown as AuthErrorCode;
      if (err_code) {
        const err_code_info = getAuthErrorCodes(err_code);
        if (err_code_info) {
          return navigateTo(localePath(`/${err_code_info.redirectType}?err_code=${err_code}`));
        }
        if (from_url?.includes('signup') || from_url?.includes('Signup')) {
          return navigateTo(localePath(`/signup?err_code=${err_code}`));
        }
        // 保留 guest_user_uuid 和其他 query 參數
        const loginQuery: Record<string, string> = { err_code: String(err_code) };
        if (to.query.guest_user_uuid) {
          loginQuery.guest_user_uuid = String(to.query.guest_user_uuid);
        }
        if (to.query.invite_code) {
          loginQuery.invite_code = String(to.query.invite_code);
        }
        return navigateTo({
          path: localePath('/login'),
          query: loginQuery,
        });
      }
      // 如果有 guest_user_uuid，保留它並傳遞到 login 頁面
      if (isGuestUserCallback) {
        return navigateTo({
          path: localePath('/login'),
          query: {
            guest_user_uuid: String(queryParams.guest_user_uuid),
            ...(queryParams.invite_code ? { invite_code: String(queryParams.invite_code) } : {}),
          },
        });
      }
      // 使用 navigateTo 而不是 window.location.href，以保留 query 參數
      return navigateTo(localePath('/login'));
    }
    return true;
  }
});
